'use client';

import { consoleLog } from '@/utils';
import { loadManagedUserScorecardContext } from '@/services';
import { useCallback } from 'react';
import type { IGlobalStateSidebarScorecard } from '@/store/types';
import type { IManagedUserSession, IManagedUserSummary } from '@/types';

/**
 * Internal dependency contract for this hook.
 *
 * Why this exists:
 * - Keeps production behavior wired to real implementations.
 * - Enables deterministic unit tests by injecting mocked functions.
 * - Avoids module-level mocking in tests when not necessary.
 */
interface UseManagedUserScorecardHandlerDependencies {
  /** Loads complete sidebar scorecard context for a managed user session. */
  loadManagedUserScorecardContext: typeof loadManagedUserScorecardContext;
  /** Error logger abstraction for observability and test assertions. */
  logError: (...args: unknown[]) => void;
  /** Warning logger abstraction for guard-clause assertions. */
  logWarn: (...args: unknown[]) => void;
}

/**
 * Production dependency bundle.
 *
 * This object is intentionally centralized so tests can pass a partial/fully
 * mocked alternative by supplying the `dependencies` param.
 */
const defaultDependencies: UseManagedUserScorecardHandlerDependencies = {
  loadManagedUserScorecardContext,
  logError: (...args) => consoleLog.error(...args),
  logWarn: (...args) => consoleLog.warn(...args),
};

/**
 * Input contract for the hook.
 *
 * The caller provides the managed-user context and the two global-store setters
 * required to drive sidebar + loading state transitions.
 */
export interface UseManagedUserScorecardHandlerParams {
  /** Optional override for API/mapping/logging dependencies (primarily for tests). */
  dependencies?: UseManagedUserScorecardHandlerDependencies;
  /** Managed user currently in context; required to open scorecards safely. */
  managedUser?: IManagedUserSummary | null;
  /** Display name shown in sidebar header when scorecard is loaded. */
  managedUserFullName: string;
  /** Managed user id required by `getUserAvailables`. */
  managedUserId?: string | null;
  /** Store action to toggle scorecard loading indicator. */
  setManagerActiveUserScorecardIsLoading?: (isLoading: boolean) => void;
  /** Store action that writes scorecard sidebar payload. */
  setSidebarScorecard?: (sidebarScorecard: IGlobalStateSidebarScorecard | null) => void;
}

export interface UseManagedUserScorecardHandlerResult {
  /**
   * Handler to be called when a user clicks "View Scorecard" in the UI, responsible for:
   * 1. Validating input parameters and logging warnings if something is amiss
   * 2. Setting loading state for the scorecard sidebar
   * 3. Fetching the relevant scorecard and availables data from the API
   * 4. Mapping API data to the frontend format
   * 5. Populating the scorecard sidebar with the fetched data
   * 6. Handling any errors that occur during the fetch/map process
   *
   * Consumers of this hook can call `handleViewScoreCardClick` with a session record,
   * and the hook will take care of the entire flow described above.
   */
  handleViewScoreCardClick: (record: IManagedUserSession) => Promise<void>;
}

/**
 * Encapsulates the scorecard sidebar loading flow as a standalone, testable hook.
 *
 * High-level flow:
 * 1) Validate input context (`record`, `managedUser`, `managedUserId`)
 * 2) Set loading state and clear sidebar content (skeleton/blank state)
 * 3) Fetch scorecard + availables in parallel
 * 4) Map API payloads to frontend entities
 * 5) Resolve linked simulation + certification from mapped collections
 * 6) Populate sidebar with final hydrated payload
 * 7) Ensure loading is turned off in `finally`
 */
export const useManagedUserScorecardHandler = ({
  dependencies = defaultDependencies,
  managedUser,
  managedUserFullName,
  managedUserId,
  setManagerActiveUserScorecardIsLoading,
  setSidebarScorecard,
}: UseManagedUserScorecardHandlerParams): UseManagedUserScorecardHandlerResult => {
  /**
   * Primary callback used by UI tables/cards when a "View Scorecard" action is
   * triggered.
   *
   * It is memoized so children receiving this handler as a prop do not re-render
   * unless one of its true runtime dependencies changes.
   */
  const handleViewScoreCardClick = useCallback(
    async (record: IManagedUserSession) => {
      // Guard: no record provided.
      // This avoids invoking API calls with `undefined` session ids.
      if (!record) {
        dependencies?.logWarn('handleViewScoreCardClick called without a scoreId');
        return;
      }

      const sessionId = record.sessionId;

      // Guard: if managed user context is missing, we cannot safely render user-specific sidebar data.
      if (!managedUser) {
        dependencies?.logWarn(
          `handleViewScoreCardClick(${sessionId}) called but managedUser is undefined`,
        );

        return;
      }

      // Guard: `managedUserId` is required by the availables endpoint.
      if (!managedUserId) {
        dependencies?.logWarn(
          `handleViewScoreCardClick(${sessionId}) called but managedUserId is undefined`,
        );

        return;
      }

      // Enter loading state immediately so UI can show progress feedback.
      setManagerActiveUserScorecardIsLoading?.(true);

      // Reset sidebar payload first to prevent stale content flash while loading.
      setSidebarScorecard?.({
        active: true,
        certification: null,
        score: null,
        simulation: null,
        title: null,
        userName: null,
      });

      try {
        const formattedContext = await dependencies?.loadManagedUserScorecardContext({
          managedUserId,
          sessionId: Number(record?.sessionId),
        });

        // Write final hydrated sidebar payload for UI rendering.
        setSidebarScorecard?.({
          active: true,
          score: formattedContext.score,
          simulation: formattedContext.simulation,
          certification: formattedContext.certification,
          title: null,
          userName: managedUserFullName,
        });
      } catch (error) {
        // Preserve existing error visibility contract for diagnostics.
        dependencies?.logError('Error fetching scorecard for session ID', record?.sessionId, error);
        return;
      } finally {
        // Always clear loading state, including all early-failure scenarios.
        setManagerActiveUserScorecardIsLoading?.(false);
      }
    },
    [
      // =================================================================
      // Runtime dependencies
      // (changes to any of these will re-create the handler)
      // ===================================================================
      dependencies,
      managedUser,
      managedUserFullName,
      managedUserId,
      setManagerActiveUserScorecardIsLoading,
      setSidebarScorecard,
    ],
  );

  // =================================================================
  // Stable hook API
  // =================================================================
  return {
    handleViewScoreCardClick,
  };
};
